"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import useBackendUrl from "@/hooks/useBackendUrl";

const DealerVehicleMarketContext = createContext({
  enabled: false,
  buyerFeeAmount: null,
  loading: true,
  refresh: async () => false,
});

export default function DealerVehicleMarketProvider({
  children,
  initialEnabled = false,
}) {
  const api = useBackendUrl();
  const [enabled, setEnabled] = useState(initialEnabled === true);
  const [buyerFeeAmount, setBuyerFeeAmount] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!api) return false;
    setLoading(true);
    try {
      const response = await fetch(`${api}/dealer-vehicle-market/status`, {
        cache: "no-store",
      });
      const data = await response.json().catch(() => ({}));
      const nextEnabled = response.ok && data?.enabled === true;
      setEnabled(nextEnabled);
      const amount = data?.feePolicy?.amount;
      setBuyerFeeAmount(
        response.ok && Number.isFinite(amount) && amount >= 0 ? amount : null,
      );
      return nextEnabled;
    } catch {
      // Fail closed: selling surfaces must not appear when the central
      // availability status cannot be verified.
      setEnabled(false);
      setBuyerFeeAmount(null);
      return false;
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(refresh, 10_000);
    const resume = () => {
      if (document.visibilityState !== "hidden") void refresh();
    };
    window.addEventListener("focus", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [refresh]);

  const value = useMemo(
    () => ({ enabled, buyerFeeAmount, loading, refresh }),
    [enabled, buyerFeeAmount, loading, refresh],
  );

  return (
    <DealerVehicleMarketContext.Provider value={value}>
      {children}
    </DealerVehicleMarketContext.Provider>
  );
}

export function useDealerVehicleMarket() {
  return useContext(DealerVehicleMarketContext);
}
